import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Zap, TrendingUp, TrendingDown, BarChart3, Activity, Target, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, BarChart, Bar, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${API}/api/v2`;

const PAIRS = ['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD'];
const STRATEGIES = [
  { value: 'rsi', label: 'RSI', desc: 'Buy when RSI is oversold, sell when overbought' },
  { value: 'ema', label: 'EMA Crossover', desc: 'Buy on golden cross, sell on death cross' },
  { value: 'macd', label: 'MACD', desc: 'Buy/sell on MACD-signal line crossovers' },
];
const TIMEFRAMES = [
  { value: 60, label: '1H' },
  { value: 360, label: '6H' },
  { value: 1440, label: '1D' },
  { value: 4320, label: '3D' },
  { value: 10080, label: '7D' },
  { value: 20160, label: '14D' },
  { value: 43200, label: '30D' },
];

const defaultParams = {
  rsi_period: 14, rsi_oversold: 30, rsi_overbought: 70,
  trade_size_pct: 10, min_profit_pct: 1.0,
  ema_fast: 9, ema_slow: 21,
  macd_fast: 12, macd_slow: 26, macd_signal: 9,
};

export default function BacktestPage() {
  const navigate = useNavigate();
  const [pair, setPair] = useState('BTC-USD');
  const [strategy, setStrategy] = useState('rsi');
  const [minutes, setMinutes] = useState(10080);
  const [initialUsd, setInitialUsd] = useState(1000);
  const [params, setParams] = useState({ ...defaultParams });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Sweep mode
  const [sweepMode, setSweepMode] = useState(false);
  const [sweepParams, setSweepParams] = useState({});
  const [sweepResults, setSweepResults] = useState(null);
  const [sweepLoading, setSweepLoading] = useState(false);

  // Trade list expand
  const [showAllTrades, setShowAllTrades] = useState(false);

  const updateParam = (key, val) => setParams(p => ({ ...p, [key]: parseFloat(val) || 0 }));

  const runBacktest = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const resp = await axios.post(`${API_V2}/backtest/run`, {
        pair, strategy, minutes, initial_usd: initialUsd, params,
      });
      if (resp.data.error) {
        toast.error(resp.data.error);
      } else {
        setResult(resp.data);
        toast.success(`Backtest complete: ${resp.data.summary.total_return_pct}% return`);
      }
    } catch (e) {
      toast.error('Backtest failed: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [pair, strategy, minutes, initialUsd, params]);

  const runSweep = useCallback(async () => {
    if (Object.keys(sweepParams).length === 0) {
      toast.error('Configure at least one sweep parameter');
      return;
    }
    setSweepLoading(true);
    setSweepResults(null);
    try {
      const resp = await axios.post(`${API_V2}/backtest/sweep`, {
        pair, strategy, minutes, initial_usd: initialUsd,
        fixed_params: params, sweep_params: sweepParams,
      });
      if (resp.data.error) {
        toast.error(resp.data.error);
      } else {
        setSweepResults(resp.data);
        toast.success(`Sweep complete: ${resp.data.combinations_tested} combinations tested`);
      }
    } catch (e) {
      toast.error('Sweep failed: ' + (e.response?.data?.detail || e.message));
    }
    setSweepLoading(false);
  }, [pair, strategy, minutes, initialUsd, params, sweepParams]);

  const copyToLive = useCallback(async (paramsToCopy) => {
    const settings = {};
    if (paramsToCopy.rsi_oversold != null) settings.rsi_oversold = paramsToCopy.rsi_oversold;
    if (paramsToCopy.rsi_overbought != null) settings.rsi_overbought = paramsToCopy.rsi_overbought;
    if (paramsToCopy.trade_size_pct != null) settings.trade_percent = paramsToCopy.trade_size_pct;
    if (paramsToCopy.min_profit_pct != null) settings.min_sell_profit_percent = paramsToCopy.min_profit_pct;
    try {
      await axios.post(`${API_V2}/user/settings/update`, settings, { withCredentials: true });
      toast.success('Settings copied to live bot!');
    } catch (e) {
      toast.error('Failed to copy settings: ' + (e.response?.data?.detail || e.message));
    }
  }, []);

  const toggleSweepParam = (name, defaults) => {
    setSweepParams(prev => {
      const next = { ...prev };
      if (next[name]) { delete next[name]; } else { next[name] = defaults; }
      return next;
    });
  };

  const updateSweep = (name, field, val) => {
    setSweepParams(prev => ({
      ...prev,
      [name]: { ...prev[name], [field]: parseFloat(val) || 0 },
    }));
  };

  const s = result?.summary;
  const trades = result?.trades || [];
  const displayTrades = showAllTrades ? trades : trades.slice(-20);

  return (
    <div className="min-h-screen bg-black text-[#00FF41] font-mono" data-testid="backtest-page">
      <Toaster theme="dark" position="top-right" />

      {/* Header */}
      <header className="border-b border-[#003B00] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-[#00FF41] hover:bg-[#003B00] h-8 px-2" data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4 mr-1" /> DASHBOARD
          </Button>
          <span className="text-lg font-bold tracking-wider">BACKTEST ENGINE</span>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto p-4 grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left: Controls */}
        <div className="lg:col-span-1 space-y-4">
          {/* Pair Selector */}
          <div className="border border-[#003B00] p-3">
            <label className="text-xs text-[#008F11] uppercase tracking-wider">Trading Pair</label>
            <div className="flex flex-wrap gap-1 mt-2">
              {PAIRS.map(p => (
                <button key={p} onClick={() => setPair(p)}
                  className={`px-2 py-1 text-xs transition-colors ${pair === p ? 'bg-[#00FF41] text-black font-bold' : 'bg-[#001a00] text-[#00FF41] hover:bg-[#003B00]'}`}
                  data-testid={`pair-${p}`}>{p.split('-')[0]}</button>
              ))}
            </div>
          </div>

          {/* Strategy */}
          <div className="border border-[#003B00] p-3">
            <label className="text-xs text-[#008F11] uppercase tracking-wider">Strategy</label>
            <div className="space-y-2 mt-2">
              {STRATEGIES.map(st => (
                <button key={st.value} onClick={() => setStrategy(st.value)}
                  className={`w-full text-left px-3 py-2 text-xs transition-colors border ${strategy === st.value ? 'border-[#00FF41] bg-[#003B00]' : 'border-[#001a00] hover:border-[#003B00]'}`}
                  data-testid={`strategy-${st.value}`}>
                  <span className="font-bold">{st.label}</span>
                  <span className="block text-[#008F11] text-[10px] mt-0.5">{st.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Timeframe */}
          <div className="border border-[#003B00] p-3">
            <label className="text-xs text-[#008F11] uppercase tracking-wider">Timeframe</label>
            <div className="flex flex-wrap gap-1 mt-2">
              {TIMEFRAMES.map(tf => (
                <button key={tf.value} onClick={() => setMinutes(tf.value)}
                  className={`px-2 py-1 text-xs ${minutes === tf.value ? 'bg-[#00FF41] text-black font-bold' : 'bg-[#001a00] text-[#00FF41] hover:bg-[#003B00]'}`}
                  data-testid={`tf-${tf.label}`}>{tf.label}</button>
              ))}
            </div>
          </div>

          {/* Parameters */}
          <div className="border border-[#003B00] p-3">
            <label className="text-xs text-[#008F11] uppercase tracking-wider">Parameters</label>
            <div className="space-y-2 mt-2">
              <ParamInput label="Initial USD" value={initialUsd} onChange={v => setInitialUsd(parseFloat(v) || 1000)} testId="initial-usd" />
              <ParamInput label="Trade Size %" value={params.trade_size_pct} onChange={v => updateParam('trade_size_pct', v)} testId="trade-size-pct" />
              <ParamInput label="Min Profit %" value={params.min_profit_pct} onChange={v => updateParam('min_profit_pct', v)} testId="min-profit-pct" />

              {strategy === 'rsi' && (
                <>
                  <ParamInput label="RSI Period" value={params.rsi_period} onChange={v => updateParam('rsi_period', v)} testId="rsi-period" />
                  <ParamInput label="RSI Oversold" value={params.rsi_oversold} onChange={v => updateParam('rsi_oversold', v)} testId="rsi-oversold" />
                  <ParamInput label="RSI Overbought" value={params.rsi_overbought} onChange={v => updateParam('rsi_overbought', v)} testId="rsi-overbought" />
                </>
              )}
              {strategy === 'ema' && (
                <>
                  <ParamInput label="EMA Fast" value={params.ema_fast} onChange={v => updateParam('ema_fast', v)} testId="ema-fast" />
                  <ParamInput label="EMA Slow" value={params.ema_slow} onChange={v => updateParam('ema_slow', v)} testId="ema-slow" />
                </>
              )}
              {strategy === 'macd' && (
                <>
                  <ParamInput label="MACD Fast" value={params.macd_fast} onChange={v => updateParam('macd_fast', v)} testId="macd-fast" />
                  <ParamInput label="MACD Slow" value={params.macd_slow} onChange={v => updateParam('macd_slow', v)} testId="macd-slow" />
                  <ParamInput label="Signal Period" value={params.macd_signal} onChange={v => updateParam('macd_signal', v)} testId="macd-signal" />
                </>
              )}
            </div>
          </div>

          {/* Run Buttons */}
          <Button onClick={runBacktest} disabled={loading}
            className="w-full h-10 bg-[#003B00] hover:bg-[#005500] text-[#00FF41] border border-[#00FF41] rounded-none font-bold tracking-wider"
            data-testid="run-backtest-btn">
            {loading ? <Activity className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            {loading ? 'RUNNING...' : 'RUN BACKTEST'}
          </Button>

          {/* Sweep Toggle */}
          <div className="border border-[#003B00] p-3">
            <div className="flex items-center justify-between">
              <label className="text-xs text-[#008F11] uppercase tracking-wider">Parameter Sweep</label>
              <button onClick={() => setSweepMode(!sweepMode)} className="text-xs text-cyan-400 hover:underline" data-testid="toggle-sweep">
                {sweepMode ? 'HIDE' : 'SHOW'}
              </button>
            </div>
            {sweepMode && (
              <div className="mt-2 space-y-2">
                <p className="text-[10px] text-[#008F11]">Select params to sweep and set min/max/step:</p>
                {strategy === 'rsi' && (
                  <>
                    <SweepToggle name="rsi_oversold" label="RSI Oversold" active={sweepParams} defaults={{ min: 20, max: 40, step: 5 }}
                      onToggle={toggleSweepParam} onUpdate={updateSweep} />
                    <SweepToggle name="rsi_overbought" label="RSI Overbought" active={sweepParams} defaults={{ min: 60, max: 80, step: 5 }}
                      onToggle={toggleSweepParam} onUpdate={updateSweep} />
                  </>
                )}
                {strategy === 'ema' && (
                  <>
                    <SweepToggle name="ema_fast" label="EMA Fast" active={sweepParams} defaults={{ min: 5, max: 15, step: 2 }}
                      onToggle={toggleSweepParam} onUpdate={updateSweep} />
                    <SweepToggle name="ema_slow" label="EMA Slow" active={sweepParams} defaults={{ min: 15, max: 30, step: 3 }}
                      onToggle={toggleSweepParam} onUpdate={updateSweep} />
                  </>
                )}
                {strategy === 'macd' && (
                  <>
                    <SweepToggle name="macd_fast" label="MACD Fast" active={sweepParams} defaults={{ min: 8, max: 16, step: 2 }}
                      onToggle={toggleSweepParam} onUpdate={updateSweep} />
                    <SweepToggle name="macd_slow" label="MACD Slow" active={sweepParams} defaults={{ min: 20, max: 32, step: 3 }}
                      onToggle={toggleSweepParam} onUpdate={updateSweep} />
                  </>
                )}
                <SweepToggle name="trade_size_pct" label="Trade Size %" active={sweepParams} defaults={{ min: 5, max: 50, step: 5 }}
                  onToggle={toggleSweepParam} onUpdate={updateSweep} />
                <Button onClick={runSweep} disabled={sweepLoading}
                  className="w-full h-8 bg-cyan-900 hover:bg-cyan-800 text-cyan-300 border border-cyan-500 rounded-none text-xs font-bold"
                  data-testid="run-sweep-btn">
                  {sweepLoading ? <Activity className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  {sweepLoading ? 'SWEEPING...' : 'RUN SWEEP'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="lg:col-span-3 space-y-4">
          {!result && !sweepResults && (
            <div className="border border-[#003B00] p-12 text-center" data-testid="empty-state">
              <Target className="h-12 w-12 mx-auto mb-4 text-[#003B00]" />
              <p className="text-[#008F11] text-sm">Configure parameters and run a backtest to see results</p>
              <p className="text-[#003B00] text-xs mt-2">Uses real historical candle data from Binance / Coinbase</p>
            </div>
          )}

          {/* Summary Cards */}
          {s && (
            <div data-testid="results-summary">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatCard label="Total Return" value={`${s.total_return >= 0 ? '+' : ''}$${s.total_return.toFixed(2)}`} sub={`${s.total_return_pct >= 0 ? '+' : ''}${s.total_return_pct.toFixed(2)}%`} positive={s.total_return >= 0} />
                <StatCard label="Win Rate" value={`${s.win_rate}%`} sub={`${s.winning_trades}W / ${s.losing_trades}L`} positive={s.win_rate >= 50} />
                <StatCard label="Max Drawdown" value={`${s.max_drawdown_pct.toFixed(2)}%`} sub="Peak to trough" positive={s.max_drawdown_pct < 5} />
                <StatCard label="Total Trades" value={s.total_trades} sub={`${s.buy_count} buys / ${s.sell_count} sells`} positive />
              </div>
              <Button onClick={() => copyToLive(result.params)}
                className="mt-2 h-8 bg-[#001a00] hover:bg-[#003B00] text-[#00FF41] border border-[#00FF41] rounded-none text-xs font-bold tracking-wider"
                data-testid="copy-to-live-btn">
                <Copy className="h-3 w-3 mr-1.5" /> COPY THESE SETTINGS TO LIVE BOT
              </Button>
            </div>
          )}

          {/* Equity Curve */}
          {result?.equity_curve && (
            <div className="border border-[#003B00] p-3" data-testid="equity-chart">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#008F11] uppercase tracking-wider">Equity Curve</span>
                <span className="text-xs text-[#003B00]">{result.candle_count} candles</span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={result.equity_curve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#003B00" />
                  <XAxis dataKey="timestamp" tick={{ fill: '#008F11', fontSize: 9 }} tickFormatter={t => {
                    const d = new Date(t);
                    return `${d.getMonth()+1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,'0')}`;
                  }} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: '#008F11', fontSize: 10 }} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={{ background: '#001a00', border: '1px solid #003B00', color: '#00FF41', fontSize: 11 }}
                    formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === 'equity' ? 'Portfolio' : name]} />
                  <ReferenceLine y={initialUsd} stroke="#FFAA00" strokeDasharray="5 5" label={{ value: 'Initial', fill: '#FFAA00', fontSize: 10 }} />
                  <Area type="monotone" dataKey="equity" stroke="#00FF41" fill="#00FF4115" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Trade List */}
          {trades.length > 0 && (
            <div className="border border-[#003B00] p-3" data-testid="trade-list">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#008F11] uppercase tracking-wider">Trades ({trades.length})</span>
                {trades.length > 20 && (
                  <button onClick={() => setShowAllTrades(!showAllTrades)} className="text-xs text-cyan-400 hover:underline flex items-center gap-1" data-testid="toggle-trades">
                    {showAllTrades ? <><ChevronUp className="h-3 w-3" /> SHOW LESS</> : <><ChevronDown className="h-3 w-3" /> SHOW ALL</>}
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#003B00] text-[#008F11]">
                      <th className="text-left py-1 px-2">Time</th>
                      <th className="text-left py-1 px-2">Type</th>
                      <th className="text-right py-1 px-2">Price</th>
                      <th className="text-right py-1 px-2">Amount</th>
                      <th className="text-right py-1 px-2">USD</th>
                      <th className="text-right py-1 px-2">RSI</th>
                      <th className="text-right py-1 px-2">P/L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayTrades.map((t, i) => (
                      <tr key={i} className="border-b border-[#001a00] hover:bg-[#001a00]">
                        <td className="py-1 px-2 text-[#008F11]">{new Date(t.timestamp).toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</td>
                        <td className={`py-1 px-2 font-bold ${t.type === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>{t.type}</td>
                        <td className="py-1 px-2 text-right">${t.price < 1 ? t.price.toFixed(6) : t.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-1 px-2 text-right">{t.amount.toFixed(6)}</td>
                        <td className="py-1 px-2 text-right">${t.usd_value.toFixed(2)}</td>
                        <td className="py-1 px-2 text-right">{t.rsi ?? '-'}</td>
                        <td className={`py-1 px-2 text-right font-bold ${t.profit != null ? (t.profit >= 0 ? 'text-green-400' : 'text-red-400') : ''}`}>
                          {t.profit != null ? `${t.profit >= 0 ? '+' : ''}$${t.profit.toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sweep Results */}
          {sweepResults && (
            <div className="border border-cyan-900 p-3" data-testid="sweep-results">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-cyan-400 uppercase tracking-wider">Sweep Results ({sweepResults.combinations_tested} combos)</span>
              </div>
              {/* Bar chart of top results */}
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={sweepResults.results.slice(0, 20)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#003B00" />
                  <XAxis dataKey="params" tick={false} />
                  <YAxis tick={{ fill: '#008F11', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: '#001a00', border: '1px solid #003B00', color: '#00FF41', fontSize: 11 }}
                    formatter={(v) => [`${v.toFixed(2)}%`, 'Return']}
                    labelFormatter={(_, payload) => {
                      if (payload?.[0]?.payload?.params) {
                        return Object.entries(payload[0].payload.params).map(([k, v]) => `${k}: ${v}`).join(', ');
                      }
                      return '';
                    }} />
                  <Bar dataKey="total_return_pct" name="Return %">
                    {(sweepResults.results.slice(0, 20)).map((entry, idx) => (
                      <Cell key={idx} fill={entry.total_return_pct >= 0 ? '#00FF41' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {/* Table */}
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-cyan-900 text-cyan-400">
                      <th className="text-left py-1 px-2">#</th>
                      <th className="text-left py-1 px-2">Params</th>
                      <th className="text-right py-1 px-2">Return %</th>
                      <th className="text-right py-1 px-2">Win Rate</th>
                      <th className="text-right py-1 px-2">Trades</th>
                      <th className="text-right py-1 px-2">Max DD</th>
                      <th className="text-right py-1 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sweepResults.results.slice(0, 20).map((r, i) => (
                      <tr key={i} className={`border-b border-[#001a00] ${i === 0 ? 'bg-[#003B00]' : 'hover:bg-[#001a00]'}`}>
                        <td className="py-1 px-2 text-[#008F11]">{i + 1}</td>
                        <td className="py-1 px-2">{Object.entries(r.params).filter(([k]) => Object.keys(sweepResults.results[0]?.params || {}).includes(k)).map(([k, v]) => `${k}=${v}`).join(', ')}</td>
                        <td className={`py-1 px-2 text-right font-bold ${r.total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>{r.total_return_pct.toFixed(2)}%</td>
                        <td className="py-1 px-2 text-right">{r.win_rate}%</td>
                        <td className="py-1 px-2 text-right">{r.total_trades}</td>
                        <td className="py-1 px-2 text-right text-red-400">{r.max_drawdown_pct.toFixed(2)}%</td>
                        <td className="py-1 px-2 text-right">
                          <button onClick={() => copyToLive(r.params)}
                            className="text-[10px] text-cyan-400 hover:text-cyan-200 hover:underline whitespace-nowrap"
                            data-testid={`sweep-copy-${i}`}>
                            <Copy className="h-3 w-3 inline mr-0.5" />USE
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Small helper components
function ParamInput({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-[10px] text-[#008F11] whitespace-nowrap">{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        className="w-20 bg-[#001a00] border border-[#003B00] text-[#00FF41] text-xs px-2 py-1 text-right focus:border-[#00FF41] outline-none"
        data-testid={testId} />
    </div>
  );
}

function SweepToggle({ name, label, active, defaults, onToggle, onUpdate }) {
  const isActive = !!active[name];
  return (
    <div className="border border-[#001a00] p-2">
      <button onClick={() => onToggle(name, defaults)} className="flex items-center gap-2 w-full text-left" data-testid={`sweep-toggle-${name}`}>
        <div className={`w-3 h-3 border ${isActive ? 'bg-cyan-400 border-cyan-400' : 'border-[#003B00]'}`} />
        <span className="text-[10px]">{label}</span>
      </button>
      {isActive && (
        <div className="flex gap-2 mt-1 pl-5">
          {['min', 'max', 'step'].map(f => (
            <div key={f} className="flex-1">
              <label className="text-[8px] text-[#008F11] uppercase">{f}</label>
              <input type="number" value={active[name]?.[f] ?? ''} onChange={e => onUpdate(name, f, e.target.value)}
                className="w-full bg-[#001a00] border border-[#003B00] text-cyan-300 text-[10px] px-1 py-0.5 text-right outline-none"
                data-testid={`sweep-${name}-${f}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, positive }) {
  return (
    <div className="border border-[#003B00] p-3 bg-[#001a00]">
      <div className="text-[10px] text-[#008F11] uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-bold mt-1 ${positive ? 'text-green-400' : 'text-red-400'}`}>{value}</div>
      <div className="text-[10px] text-[#008F11] mt-0.5">{sub}</div>
    </div>
  );
}
